/**
 * Lesson gating — pure learner-side enforcement rule.
 *
 * A "gated" lesson (`Lesson.isGated`) requires ALL previous lessons in the
 * curriculum to be completed before a learner can access it. Useful for
 * mid-course quizzes, end-of-course surveys, and certificate pages —
 * anything where the admin wants free-flow lessons up to a point and then
 * a mandatory checkpoint before advancing.
 *
 * Gate state lives on the lesson itself (`Lesson.isGated`); this module only
 * holds the locking rule so the sidebar and the player can't drift on it.
 */

interface GateLesson {
  id?: number | string | null;
  isGated?: boolean;
}

/**
 * Learner-side enforcement. Given the course's lessons in curriculum order
 * and the ids the learner has completed, returns the set of lesson ids that
 * are currently LOCKED by a gate.
 *
 * Rule (see file header): a gated lesson stays locked until EVERY lesson
 * positioned before it is complete. Non-gated lessons are never locked by
 * this function. Multiple gates cascade naturally — because a learner can't
 * complete a locked lesson, an earlier gate blocks the "all preceding
 * complete" check for every later gate until it's cleared.
 *
 * The returned set holds string ids so callers should compare with String(id).
 */
export function getGateLockedLessonIds(
  orderedLessons: GateLesson[],
  completedLessonIds: Array<number | string>,
): Set<string> {
  const completed = new Set(completedLessonIds.map((id) => String(id)));
  const locked = new Set<string>();
  for (let i = 0; i < orderedLessons.length; i++) {
    if (orderedLessons[i].isGated !== true) continue;
    const allPrecedingComplete = orderedLessons
      .slice(0, i)
      .every((prev) => completed.has(String(prev.id)));
    if (!allPrecedingComplete) locked.add(String(orderedLessons[i].id));
  }
  return locked;
}
